import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TaggedSubstationService } from '../../../services/tagged-substation.service';
import { CimSubstationService } from '../../../services/cim-substation.service';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { slideToggle } from '../../../helpers/slidetoggle';
import {
  CimSubstationDataQuality,
  EditCimSubstation,
  IncomingAcLineDetails,
} from '../../../models/cim-substation.model';
import { EntityModelCustomizationService } from '../../../services/entity-model-customization.service';
import { EMCType } from '../../../enums/emc-type';
import { EntityModelConfiguration } from '../../../enums/entityModelConfiguration';
import {
  EditedAttributeValues,
  SectionDefinitionEntityType,
} from '../../../models/section-definition.model';
import { SectionInstance } from '../../../models/section-definition.model';
import { AssignTaskModalComponent } from '../../common/assign-task-modal/assign-task-modal.component';
import { AttributeValue } from '../../../models/section-definition.model';
import { AssetStatusEnum } from '../../../models/asset-status.model';
import { UtilitySharedService } from '../../../services/shared/utility-shared.service';
import { ActivatedRoute } from '@angular/router';
import { PermissionsService } from '../../../services/permissions.service';
import { SubstationTemplateService } from '../../../services/substation-template.service';
import { Purpose } from '../../../enums/purpose';
import { EmcOptions } from '../../../models/entity-model-customization.model';
import { AttributeValidationError } from '../../common/section-instance/attribute-rows/attribute-number-edit-row/attribute-number-edit-row.component';
import { getAttributeEdit } from '../../../helpers/section-instance-edit.helper';
import { DmcService } from '../../../services/dmc.service';
import { MeterBrandService } from '../../../services/meter-brand.service';
import { StaticDropdownOption } from '../../common/static-field-rows/static-dropdown-edit-row/static-dropdown-edit-row.component';
import { AssetRatingService } from '../../../services/asset-rating.service';
import { CimClass } from '../../../models/network-structure.model';
import { forkJoin } from 'rxjs';

interface EditedStaticValues {
  MeterInstalled?: boolean | null;
  MeterNumber?: string | null;
  IsCableLengthEnough?: boolean | null;
  ConditionOfMeter?: string | null;
  MeterCommunicationSystem?: string | null;
  MeterBrand?: number | null;
  ProposedLocation?: string | null;
}

type StaticPropertyName = keyof EditedStaticValues;

@Component({
  selector: 'lib-edit-cim-substation',
  templateUrl: './edit-cim-substation.component.html',
  styleUrl: './edit-cim-substation.component.scss',
  standalone: false,
  animations: [slideToggle],
})
export class EditCimSubstationComponent implements OnInit {
  headerExpanded: boolean = false;
  selectedSubstation: EditCimSubstation = new EditCimSubstation();
  editEnabled: boolean = false;
  networkCardExpanded: boolean = true;
  dsMeterCardExpanded: boolean = true;
  userActionsCardExpanded: boolean = true;
  isMapVisible: boolean = false;
  utilityId: number = 0;
  mrid: string = '';
  substationTemplateId: number | undefined = undefined; // Changed from number | null
  @ViewChild(AssignTaskModalComponent)
  assignTaskModalComponent!: AssignTaskModalComponent;
  @ViewChild('closeReviewModal') closeReviewModal!: ElementRef<HTMLButtonElement>;
  @ViewChild('closeGisReviewModal') closeGisReviewModal!: ElementRef<HTMLButtonElement>;
  AssetAssignType = AssetStatusEnum;
  assetAssignType?: AssetStatusEnum;
  fieldAgentSelected: boolean = false;
  isDataQuality: boolean = false;
  sectionInstances: SectionInstance[] = [];
  equipmentInstances: Record<string, any[]> = {};
  title: string = '';
  EMCConfiguration: EntityModelConfiguration[] = [];
  editableAttributeIds: number[] = [];
  editedValues: EditedAttributeValues = {};
  editableStaticProperties: string[] = [];
  editedStaticValues: EditedStaticValues = {};
  staticOriginals: Required<EditedStaticValues> = {
    MeterInstalled: false,
    MeterNumber: null,
    IsCableLengthEnough: null,
    ConditionOfMeter: null,
    MeterCommunicationSystem: null,
    MeterBrand: null,
    ProposedLocation: null,
  };
  needsRepositioning: boolean | null | undefined;
  powerTransformerEditableAttributeIds: number[] = [];
  isAssetRatingEditable: boolean = false;
  editedEquipmentValues: Record<string, EditedAttributeValues> = {};
  editedAssetRatings: Record<string, number | null> = {};
  assetRatingOptions: StaticDropdownOption<number>[] = [];
  meterBrandOptions: StaticDropdownOption<number>[] = [];
  readonly yesNoOptions: StaticDropdownOption<boolean>[] = [
    { value: true, label: 'EDIT_INJECTION_SUBSTATION.DS_METER.YES' },
    { value: false, label: 'EDIT_INJECTION_SUBSTATION.DS_METER.NO' },
  ];
  readonly meterConditionOptions: StaticDropdownOption<string>[] = [
    { value: 'Working', label: 'COMMON.METER_CONDITION.WORKING' },
    { value: 'NotWorking', label: 'COMMON.METER_CONDITION.NOT_WORKING' },
    { value: 'Burnt', label: 'COMMON.METER_CONDITION.BURNT' },
    { value: 'Functional', label: 'COMMON.METER_CONDITION.FUNCTIONAL' },
    { value: 'Blank_Screen', label: 'COMMON.METER_CONDITION.BLANK_SCREEN' },
    { value: 'Obsolete', label: 'COMMON.METER_CONDITION.OBSOLETE' },
    { value: 'Faulty', label: 'COMMON.METER_CONDITION.FAULTY' },
  ];
  readonly meterCommunicationStatusOptions: StaticDropdownOption<string>[] = [
    { value: 'CommunicatingWithAmiSystem', label: 'TRANSFORMERS.EDIT.COMMUNICATING_WITH_AMI' },
    { value: 'NotCommunicatingWithAmiSystem', label: 'TRANSFORMERS.EDIT.NOT_COMMUNICATING_WITH_AMI' },
  ];
  readonly proposedLocationOptions: StaticDropdownOption<string>[] = [
    { value: 'Close to DS point', label: 'Close to DS point' },
    { value: 'Beside feeder pillar', label: 'Beside feeder pillar' },
  ];
  private readonly requiredStaticProperties: StaticPropertyName[] = [
    'MeterNumber',
    'ConditionOfMeter',
    'MeterCommunicationSystem',
    'MeterBrand',
  ];
  retagAllowed: boolean = false;
  recallAssignedTask: boolean = false;
  recallValidatedTask: boolean = false;
  recallAssignedAllowed: boolean = false;
  recallValidatedAllowed: boolean = false;
  showRecallMenu: boolean = false;
  currentIncomingAcLineIndex: number = 0;
  currentPendingIncomingAcLineIndex: number = 0;
  cimDataQuality?: CimSubstationDataQuality;
  voltageClass: string = '';
  assetStatusMap: { [key: number]: string } = {
    [AssetStatusEnum.Pending]: 'Pending',
    [AssetStatusEnum.Complete]: 'Complete',
    [AssetStatusEnum.Incomplete]: 'Incomplete',
    [AssetStatusEnum.Assigned]: 'Assigned',
    [AssetStatusEnum.Rejected]: 'Rejected',
    [AssetStatusEnum.Retagged]: 'Retagged',
  };
  items: any = [];
  constructor(
    private taggedSubstationService: TaggedSubstationService,
    private toastr: ToastrService,
    private location: Location,
    private utilitySharedService: UtilitySharedService,
    private route: ActivatedRoute,
    private emcService: EntityModelCustomizationService,
    public permissionsService: PermissionsService,
    private substationTemplateService: SubstationTemplateService,
    private dmcService: DmcService,
    private meterBrandService: MeterBrandService,
    private assetRatingService: AssetRatingService,
    private cimSubstationService: CimSubstationService,
  ) {}

  ngOnInit(): void {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
    this.route.params.subscribe((params) => {
      this.substationTemplateId = Number(params['id']) || undefined;
      this.mrid = params['guid'];
      this.getSubstationByGuid();
      this.getEditEmcOptions();
    });
    this.getEMCConfiguration();
    this.getEquipmentTemplateInstances();
    this.getSidebarSubstations();
    this.getMeterBrandOptions();
    this.getPowerTransformerEditEmcOptions();
    this.getAssetRatingOptions();
  }

  private getPowerTransformerEditEmcOptions() {
    this.emcService
      .getSelectedEmcOptions(
        SectionDefinitionEntityType.EquipmentTemplate,
        Purpose.Edit,
        this.utilityId,
        undefined,
        CimClass.PowerTransformer,
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            const options = (responseData.data ?? []) as EmcOptions[];
            this.powerTransformerEditableAttributeIds = options
              .filter((option) => option.attributeDefinitionId)
              .map((option) => option.attributeDefinitionId!);
            this.isAssetRatingEditable = options.some((option) => option.staticPropertyName === 'AssetRating');
          } else {
            this.toastr.error(responseData?.message || 'Failed to fetch EMC options');
          }
        },
        error: () => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  private getAssetRatingOptions() {
    this.assetRatingService.getAllForSelect().subscribe({
      next: (response) => {
        this.assetRatingOptions = (response?.data ?? []).map((rating: any) => ({ value: rating.id, label: rating.kvaRating }));
      },
      error: () => {
        this.toastr.error('Error occurred while loading asset ratings');
      },
    });
  }

  private getMeterBrandOptions() {
    this.meterBrandService.getAllForSelect().subscribe({
      next: (response) => {
        this.meterBrandOptions = (response.body?.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }));
      },
      error: () => {
        this.toastr.error('Error occurred while loading meter brands');
      },
    });
  }
  getSidebarSubstations() {
    if (this.utilityId === 0) return;
    this.substationTemplateService.getAllForSidebar(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        this.items = responseData ? responseData.data : [];
        const item = this.items.find((item: any) => item.id === this.substationTemplateId);
        this.voltageClass = item?.voltageClass ?? '';
      },
      error: (_) => {
        this.toastr.error('Error occurred while loading substation templates');
      },
    });
  }
  setEMCPermissions() {
    this.retagAllowed = this.EMCConfiguration.includes(
      EntityModelConfiguration.Retag,
    );
    this.recallAssignedTask = this.EMCConfiguration.includes(
      EntityModelConfiguration.RecallAssignedTask,
    );
    this.recallValidatedTask = this.EMCConfiguration.includes(
      EntityModelConfiguration.RecallValidatedTask,
    );
  }

  disableReviewButton() {
    return (
      this.selectedSubstation.reviewStatus === 'Reviewed' || this.selectedSubstation.dataQualityId === null
    );
  }

  cancelReviewModal(event: any) {
    this.closeReviewModal?.nativeElement.click();
  }

  disableGisReviewButton() {
    return this.selectedSubstation.gisReviewStatus !== 'Not Validated';
  }

  cancelGisReviewModal(event: Event) {
    this.closeGisReviewModal?.nativeElement.click();
  }

  submitGisReview(approved: boolean) {
    this.cimSubstationService
      .gisReview({ substationId: this.selectedSubstation.id, approved: approved })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.closeGisReviewModal?.nativeElement.click();
            this.toastr.success(responseData?.message);
            setTimeout(() => window.location.reload(), 1000);
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occurred');
        },
      });
  }

  private getEMCConfiguration() {
    var obj = {
      type: EMCType.SubstationTemplate,
      utilityId: null,
      substationTemplateId: this.substationTemplateId,
    };
    this.emcService.getConfigurationByType(obj).subscribe({
      next: (response: any) => {
        var data = response?.body?.data;
        this.EMCConfiguration = data.map(
          (s: any) =>
            EntityModelConfiguration[
            s as keyof typeof EntityModelConfiguration
            ],
        );
        this.setEMCPermissions();
      },
    });
  }

  private getEditEmcOptions() {
    this.emcService
      .getSelectedEmcOptions(
        SectionDefinitionEntityType.SubstationTemplate,
        Purpose.Edit,
        this.utilityId,
        this.substationTemplateId,
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            const options = (responseData.data ?? []) as EmcOptions[];
            this.editableAttributeIds = options
              .filter((option) => option.attributeDefinitionId)
              .map((option) => option.attributeDefinitionId!);
            this.editableStaticProperties = options
              .filter((option) => option.staticPropertyName)
              .map((option) => option.staticPropertyName!);
          } else {
            this.toastr.error(
              responseData?.message || 'Failed to fetch EMC options',
            );
          }
        },
        error: () => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  saveAttributeValues() {
    const substationEdit = getAttributeEdit(this.sectionInstances, this.editableAttributeIds, this.editedValues);
    const powerTransformerEdits = (this.equipmentInstances['PowerTransformer'] ?? [])
      .filter((pt) => pt.mrId in this.editedEquipmentValues || pt.mrId in this.editedAssetRatings)
      .map((pt) => ({
        pt,
        edit: getAttributeEdit(
          pt.sectionInstances ?? [],
          this.powerTransformerEditableAttributeIds,
          this.editedEquipmentValues[pt.mrId] ?? {},
        ),
      }));

    if (
      substationEdit.invalid ||
      this.hasMissingRequiredStaticValues() ||
      powerTransformerEdits.some(({ edit }) => edit.invalid)
    ) {
      this.toastr.warning('Please correct the invalid fields.');
      return;
    }

    const updates = [
      {
        assetType: SectionDefinitionEntityType.SubstationTemplate,
        assetGuid: this.selectedSubstation.mrid,
        attributeValues: substationEdit.changed,
        staticValues: this.getChangedStaticValues(),
      },
      ...powerTransformerEdits.map(({ pt, edit }) => ({
        assetType: SectionDefinitionEntityType.EquipmentTemplate,
        assetGuid: pt.mrId as string,
        attributeValues: edit.changed,
        staticValues: this.getChangedAssetRating(pt),
      })),
    ].filter((dataIn) => dataIn.attributeValues.length > 0 || dataIn.staticValues.length > 0);

    if (updates.length === 0) {
      this.editEnabled = false;
      return;
    }

    forkJoin(updates.map((dataIn) => this.dmcService.updateAttributeValues(dataIn))).subscribe({
      next: (responses) => {
        const failed = responses.find((response) => !(response.body?.status === 200 || response.body?.status === 'OK'));
        if (failed) {
          this.toastr.error(failed.body?.message);
          return;
        }
        this.toastr.success(responses[0].body?.message);
        this.editEnabled = false;
        this.editedValues = {};
        this.editedStaticValues = {};
        this.needsRepositioning = undefined;
        this.editedEquipmentValues = {};
        this.editedAssetRatings = {};
        this.getSubstationByGuid();
        this.getEquipmentTemplateInstances();
      },
      error: () => {
        this.toastr.error('Error occurred while saving substation');
      },
    });
  }

  private getChangedAssetRating(powerTransformer: any) {
    const value = this.editedAssetRatings[powerTransformer.mrId];
    return this.isAssetRatingEditable && value !== undefined && value !== (powerTransformer.assetRatingId ?? null)
      ? [{ staticPropertyName: 'AssetRating', intValue: value }]
      : [];
  }

  isStaticEditable(property: StaticPropertyName): boolean {
    return this.editEnabled && this.editableStaticProperties.includes(property);
  }

  get isMeterInstalledEditable(): boolean {
    return this.isStaticEditable('MeterInstalled') && this.voltageClass == 'LV';
  }

  get isMeterInstalled(): boolean {
    return this.editedStaticValues.MeterInstalled ?? this.staticOriginals.MeterInstalled ?? false;
  }

  get isAddingMeter(): boolean {
    return this.isMeterInstalled && !this.selectedSubstation.meterId;
  }

  get isRemovingMeter(): boolean {
    return !this.isMeterInstalled && !!this.selectedSubstation.meterId;
  }

  get addMeterError(): AttributeValidationError | null {
    return this.isAddingMeter &&
      !(this.editableStaticProperties.includes('MeterNumber') && this.editableStaticProperties.includes('MeterBrand'))
      ? { key: 'COMMON.ATTRIBUTE_VALIDATION.DS_METER_NOT_ADDABLE' }
      : null;
  }

  get showProposedLocationEdit(): boolean {
    return this.needsRepositioning === undefined ? !!this.staticOriginals.ProposedLocation : this.needsRepositioning === true;
  }

  onRepositioningChange(value: boolean | null) {
    this.needsRepositioning = value;
    if (value) {
      delete this.editedStaticValues.ProposedLocation;
    } else {
      this.editedStaticValues.ProposedLocation = null;
    }
  }

  private setStaticOriginals() {
    const substation = this.selectedSubstation;
    const hasMeter = !!substation.meterId;
    this.staticOriginals = {
      MeterInstalled: hasMeter,
      MeterNumber: substation.meterNumber || null,
      IsCableLengthEnough: hasMeter ? substation.isCableLengthEnough : null,
      ConditionOfMeter: substation.meterCondition || null,
      MeterCommunicationSystem:
        substation.meterCommunicationStatus && substation.meterCommunicationStatus !== 'NotSelected'
          ? substation.meterCommunicationStatus
          : null,
      MeterBrand: substation.meterBrandId,
      ProposedLocation: hasMeter ? substation.proposedLocation || null : null,
    };
  }

  private hasMissingRequiredStaticValues(): boolean {
    if (this.isAddingMeter) {
      return (
        !!this.addMeterError ||
        (this.editableStaticProperties as StaticPropertyName[]).some((property) =>
          property === 'ProposedLocation'
            ? this.needsRepositioning == null || (this.needsRepositioning && this.isStaticValueEmpty(property))
            : property !== 'MeterInstalled' && this.isStaticValueEmpty(property),
        )
      );
    }
    if (!this.selectedSubstation.meterId || this.isRemovingMeter) return false;

    return (this.editableStaticProperties as StaticPropertyName[])
      .filter(
        (property) =>
          this.requiredStaticProperties.includes(property) ||
          (property === 'ProposedLocation' && this.showProposedLocationEdit),
      )
      .some((property) => this.isStaticValueEmpty(property));
  }

  private isStaticValueEmpty(property: StaticPropertyName): boolean {
    const value = property in this.editedStaticValues ? this.editedStaticValues[property] : this.staticOriginals[property];
    return value == null || value === '';
  }

  private getChangedStaticValues() {
    if (this.isRemovingMeter) return [{ staticPropertyName: 'MeterInstalled', boolValue: false }];
    if (!this.isMeterInstalled) return [];

    return (Object.keys(this.editedStaticValues) as StaticPropertyName[])
      .filter(
        (property) =>
          this.editableStaticProperties.includes(property) &&
          this.editedStaticValues[property] !== this.staticOriginals[property],
      )
      .map((property) => {
        const value = this.editedStaticValues[property];
        switch (property) {
          case 'MeterBrand':
            return { staticPropertyName: property, intValue: value as number | null };
          case 'MeterInstalled':
          case 'IsCableLengthEnough':
            return { staticPropertyName: property, boolValue: value as boolean | null };
          default:
            return { staticPropertyName: property, stringValue: value as string | null };
        }
      });
  }

  getAssetStatusString(status: AssetStatusEnum): string {
    return this.assetStatusMap[status] || 'Unknown';
  }

  getSubstationTemplateInstances() {
    this.dmcService
      .getInstancesForAsset({
        entityType: SectionDefinitionEntityType.SubstationTemplate,
        assetGuid: this.mrid,
      })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.title = responseData.data.templateName;
            this.sectionInstances = (
              responseData.data?.sectionInstances ?? []
            ).map((section: any) => ({
              ...section,
              expanded: true,
            }));

            const imageAttributes = this.sectionInstances
              .flatMap((section) => section.attributeValues)
              .filter((attr) => attr.type.toString() === 'Image');

            this.cimDataQuality = new CimSubstationDataQuality(
              this.title,
              imageAttributes,
            );
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: () => {
          this.toastr.error('Error fetching section instances');
        },
      });
  }

  getEquipmentTemplateInstances() {
    this.taggedSubstationService
      .getEquipmentForAsset(this.mrid, this.substationTemplateId)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.equipmentInstances = responseData.data;
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (error) => {
          this.toastr.error('Error fetching equipment instances');
        },
      });
  }

  getSubstationByGuid() {
    this.taggedSubstationService
      .getByGuid(this.mrid, this.utilityId)
      .subscribe({
        next: (response) => {
          this.selectedSubstation = new EditCimSubstation(response.data);
          this.setStaticOriginals();
          this.currentIncomingAcLineIndex = 0;
          this.currentPendingIncomingAcLineIndex = 0;
          if (this.selectedSubstation.assetStatus === 'Assigned') {
            this.recallAssignedAllowed = true;
          }
          if (this.selectedSubstation.dataQualityId) {
            this.recallValidatedAllowed = true;
          }
          this.getSubstationTemplateInstances();
        },
      });
  }

  backClicked() {
    this.location.back();
  }

  setClickedLocation(attributeValue: AttributeValue | undefined) {
    this.isMapVisible = !this.isMapVisible;
  }

  onAssignModalShown() {
    this.assignTaskModalComponent.title =
      this.assetAssignType == AssetStatusEnum.Retagged
        ? 'COMMON.RETAG_TASK'
        : 'COMMON.ASSIGN_AGENT';
    this.assignTaskModalComponent.submitText =
      this.assetAssignType == AssetStatusEnum.Retagged
        ? 'COMMON.RETAG'
        : 'COMMON.ASSIGN_TASK';
    this.assignTaskModalComponent.getMobileUsers();
  }

  onModalAssignFade() {
    this.assignTaskModalComponent.filterText = '';
    this.assignTaskModalComponent.isDropdownOpened = false;
  }

  selectFieldAgent(id: number) {
    this.selectedSubstation.assignedUserId = id;
    this.fieldAgentSelected = true;
  }

  assignTask = (): void => {
    if (
      this.selectedSubstation.assignedUserId != 0 &&
      this.fieldAgentSelected
    ) {
      var dataIn = {
        entityId: this.selectedSubstation.id,
        userId: this.selectedSubstation.assignedUserId,
        assetStatus: this.getAssetStatusString(this.assetAssignType!),
        isFromRejection: this.isDataQuality,
      };
      this.taggedSubstationService.assignSubstation(dataIn).subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.toastr.success('Successfully assigned user');
            window.location.reload();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occurred');
        },
      });
    } else {
      this.toastr.warning('Please select valid user.');
    }
  };

  reload(e: any) {
    window.location.reload();
  }

  recall(recallType: string) {
    this.taggedSubstationService
      .recall({ id: this.selectedSubstation.id, recallType: recallType })
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.toastr.success('Successfully recalled Substation.');
            window.location.reload();
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occurred');
        },
      });
  }

  prevIncomingAcLine() {
    if (this.currentIncomingAcLineIndex > 0) {
      this.currentIncomingAcLineIndex--;
    }
  }

  nextIncomingAcLine() {
    if (
      this.currentIncomingAcLineIndex <
      this.selectedSubstation.incomingAcLines.length - 1
    ) {
      this.currentIncomingAcLineIndex++;
    }
  }

  prevPendingIncomingAcLine() {
    if (this.currentPendingIncomingAcLineIndex > 0) {
      this.currentPendingIncomingAcLineIndex--;
    }
  }

  nextPendingIncomingAcLine() {
    if (
      this.currentPendingIncomingAcLineIndex <
      this.selectedSubstation.pendingIncomingAcLines.length - 1
    ) {
      this.currentPendingIncomingAcLineIndex++;
    }
  }

  isProposedAddition(acLine: IncomingAcLineDetails) {
    return !this.selectedSubstation.incomingAcLines.some(
      (x) => x.parentACLineId === acLine.parentACLineId
    );
  }
}
